import {
  AuthTokenResponsePassword,
  SignInWithPasswordCredentials,
  User,
} from '@supabase/supabase-js';
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { supabase } from '~/lib/supabase';
import { parseJson } from '~/utils/helpers';

interface GeneralState {
  template: string;
  data: string;
  loading: boolean;
  user: User | null;
}

interface GlobalStateContextType {
  generalState: GeneralState;
  setGeneralState: (value: GeneralState) => void;
  signIn: (
    credentials: SignInWithPasswordCredentials,
  ) => Promise<AuthTokenResponsePassword>;
  signOut: () => void;
}

interface GlobalStateProviderProps {
  children: ReactNode;
}

// Create Context with proper typing
const GlobalStateContext = createContext<GlobalStateContextType | undefined>(
  undefined,
);

// Custom hook to use global state
export const useGlobalState = (): GlobalStateContextType => {
  const context = useContext(GlobalStateContext);
  if (!context) {
    throw new Error('useGlobalState must be used within a GlobalStateProvider');
  }
  return context;
};

// Provider component
export const GlobalStateProvider: React.FC<GlobalStateProviderProps> = ({
  children,
}) => {
  const [generalState, setGeneralState] = useState<GeneralState>(
    parseJson(localStorage.generalState) || {
      template: '{}',
      data: '{}',
      loading: false,
    },
  );

  React.useEffect(() => {
    localStorage.setItem('generalState', JSON.stringify(generalState));
  }, [generalState]);

  React.useEffect(() => {
    setGeneralState((prev) => ({ ...prev, loading: true }));

    supabase.auth.getSession().then(({ data: { session } }) => {
      setGeneralState((prev) => ({
        ...prev,
        user: session?.user ?? null,
        loading: false,
      }));
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setGeneralState((prev) => ({
        ...prev,
        user: session?.user ?? null,
        loading: false,
      }));
    });

    return () => subscription?.unsubscribe();
  }, []);

  const signIn = async (auth: SignInWithPasswordCredentials) => {
    return await supabase.auth.signInWithPassword(auth);
  };

  const signOut = async () => {
    return await supabase.auth.signOut();
  };

  const value: GlobalStateContextType = {
    generalState,
    setGeneralState,
    signIn,
    signOut,
  };

  return (
    <GlobalStateContext.Provider value={value}>
      {children}
    </GlobalStateContext.Provider>
  );
};
